import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { TooltipButton } from "@/components/ui/TooltipButton";
import { CreditCard, Zap, Check, Plus, Minus, DollarSign, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function BuyCredits() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const PACKAGE_PRICE = 5; // $5 per package
  const totalAmount = quantity * PACKAGE_PRICE;

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);
  };

  const handlePurchase = async () => {
    setIsProcessing(true);
    try {
      // Add balance in cents (e.g., $5 = 500 cents)
      const creditsToAdd = totalAmount * 100;
      const balanceBefore = currentUser?.premium_credits || 0;
      const balanceAfter = balanceBefore + creditsToAdd;
      
      // Update premium_credits directly in Supabase
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ premium_credits: balanceAfter })
        .eq("id", currentUser.id);

      if (updateError) throw updateError;

      // Log the transaction
      await supabase.from("credit_transactions").insert({
        user_id: currentUser.id,
        transaction_type: 'purchase',
        amount: totalAmount,
        credits_added: creditsToAdd,
        balance_before: balanceBefore,
        balance_after: balanceAfter,
        metadata: {
          amount_usd: totalAmount,
          quantity: quantity,
          package_price: PACKAGE_PRICE
        }
      });

      // Also log to archive_logs for backward compatibility
      await supabase
        .from("archive_logs")
        .insert({
          user_id: currentUser.id,
          action: "credit_purchase",
          details: {
            amount_usd: totalAmount,
            credits_added: creditsToAdd,
            quantity: quantity
          }
        });

      toast.success(`Successfully added $${totalAmount.toFixed(2)} to your balance!`);
      
      // Refresh user data
      await loadUser();
      setQuantity(1);
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Purchase failed: " + (error.message || "Unknown error"));
    } finally {
      setIsProcessing(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className="text-4xl font-bold text-foreground flex items-center gap-3">
              <CreditCard className="w-10 h-10" />
              Buy Credits
            </h1>
            <TooltipButton
              onClick={() => navigate("/PurchaseHistory")}
              variant="outline"
              size="icon"
              className="neuro-base rounded-xl"
              tooltip="View your purchase history"
            >
              <Receipt className="w-5 h-5" />
            </TooltipButton>
          </div>
          <p className="text-xl text-muted-foreground mb-4">
            Add funds to your Soundope balance to boost your tracks and get more visibility.
          </p>
          <p className="text-lg text-muted-foreground">
            Current Balance: <strong className="text-foreground">${((currentUser?.balance || 0) / 100).toFixed(2)}</strong>
          </p>
          <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-xl">
            <p className="text-sm text-yellow-200 text-center">
              ⚠️ Credit purchases are currently locked. Only purchases of exactly <strong>111 packages</strong> are allowed.
            </p>
          </div>
        </div>

        <div className="bg-card p-8 rounded-lg border border-border mb-8">
          <h2 className="text-2xl font-bold text-card-foreground mb-6 text-center">Add Balance</h2>
          
          <div className="max-w-md mx-auto">
            <div className="neuro-base rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#b0a090]" />
                  <span className="text-lg text-[#d0d0d0]">$5.00 Package</span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 mb-4">
                <TooltipButton
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  variant="outline"
                  size="icon"
                  className="neuro-base rounded-xl"
                  disabled={quantity <= 1}
                  tooltip="Decrease quantity"
                >
                  <Minus className="w-4 h-4" />
                </TooltipButton>

                <div className="neuro-pressed rounded-xl px-8 py-4 min-w-[120px] text-center">
                  <div className="text-3xl font-bold text-[#d0d0d0]">{quantity}</div>
                  <div className="text-xs text-[#808080]">
                    {quantity === 1 ? "package" : "packages"}
                  </div>
                </div>

                <TooltipButton
                  onClick={() => setQuantity(quantity + 1)}
                  variant="outline"
                  size="icon"
                  className="neuro-base rounded-xl"
                  tooltip="Increase quantity"
                >
                  <Plus className="w-4 h-4" />
                </TooltipButton>
              </div>

              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-[#b0a090] mb-2">
                  ${totalAmount.toFixed(2)}
                </div>
                <div className="text-sm text-[#808080]">
                  {quantity} × $5.00
                </div>
              </div>

              <TooltipButton
                onClick={handlePurchase}
                disabled={isProcessing || quantity !== 111}
                className="w-full neuro-base active:neuro-pressed rounded-2xl py-4 text-lg"
                tooltip={quantity !== 111 ? "Credit purchases are currently locked. Only purchases of exactly 111 packages are allowed." : "Add this amount to your Soundope balance"}
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                    <span>Processing...</span>
                  </div>
                ) : quantity !== 111 ? (
                  <div className="flex items-center justify-center gap-2">
                    <DollarSign className="w-5 h-5 opacity-50" />
                    <span className="text-[#808080]">Purchases Locked (111 packs required)</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    <span className="text-[#d0d0d0]">Purchase ${totalAmount.toFixed(2)}</span>
                  </div>
                )}
              </TooltipButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
